import React, { useRef } from 'react';
import { Button } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import moment from 'moment';
import useGeneratePDF from '../../hooks/useGeneratePDF';
import '../../styles/reports.scss';

interface Route {
  trip_id: number;
  total_deliveries: number;
  actual_deliveries: string;
  cancelled_deliveries: string;
  planned_deliveries: string;
  skipped_deliveries: string;
  delivery_start: string | null;
  delivery_end: string | null;
  departure: string | null;
  arrival: string | null;
  vehicle_id: string;
  user_id: number;
}

interface DeliveryReportProps {
  routes: Route[];
  totalPlanned: string;
  totalActual: string;
  totalCancelled: number;
  totalSkipped: number;
  successRate: string;
  totalActualDistance: number;
  // eslint-disable-next-line react/require-default-props
  dateRange?: { start?: string; end?: string };
}

const fmt = (val: string | null) => (val && moment(val).isValid() ? moment(val).format('MMM DD, YYYY HH:mm') : '-');

const successColor = (actual: number, planned: number): string => {
  if (!planned) return '#95a5a6';
  const rate = (actual / planned) * 100;
  if (rate >= 95) return '#27ae60';
  if (rate >= 90) return '#f39c12';
  return '#c0392b';
};

interface TemplateProps extends DeliveryReportProps {
  templateRef: React.RefObject<HTMLDivElement>;
}

const DeliveryReportTemplate = ({
  templateRef,
  routes,
  totalPlanned,
  totalActual,
  totalCancelled,
  totalSkipped,
  successRate,
  totalActualDistance,
  dateRange,
}: TemplateProps) => {
  const dateLabel = dateRange?.start && dateRange?.end ? `${dateRange.start} to ${dateRange.end}` : 'All Dates';

  const summaryItems: [string, string][] = [
    ['Date Range', dateLabel],
    ['Total Routes', String(routes.length)],
    ['Total Planned Deliveries', totalPlanned],
    ['Total Actual Deliveries', totalActual],
    ['Total Cancelled', String(totalCancelled)],
    ['Total Skipped', String(totalSkipped)],
    ['Overall Success Rate', `${(Number(successRate) * 100).toFixed(2)}%`],
    ['Total Actual Distance', `${Number(totalActualDistance).toFixed(2)} Km`],
  ];

  const headers = [
    'Route ID',
    'Vehicle',
    'Planned',
    'Actual',
    'Pending',
    'Cancelled',
    'Skipped',
    'Success',
    'Departure (Actual)',
    'Arrival (Actual)',
  ];

  return (
    <div
      ref={templateRef}
      className="report report--w1200"
      style={{ '--report-accent': '#1a6eb5' } as React.CSSProperties}
    >
      <div className="report__header">
        <span className="report__title">Delivery Report — Route Wise</span>
        <span className="report__generated">Generated: {new Date().toLocaleString()}</span>
      </div>

      <div className="report__section-title">Summary</div>
      <div className="report__grid report__grid--mb24">
        {summaryItems.map(([label, value]) => (
          <div key={label} className="report__row">
            <div className="report__cell-label" style={{ '--section-bg': '#dbeafe' } as React.CSSProperties}>
              {label}
            </div>
            <div className="report__cell-value">{value}</div>
          </div>
        ))}
      </div>

      <div className="report__section-title">Route Details</div>
      <table className="report__table">
        <thead>
          <tr className="report__thead-row">
            {headers.map((h) => (
              <th key={h} className="report__th report__th--nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {routes.map((r, i) => {
            const actual = Number(r.actual_deliveries);
            const planned = Number(r.total_deliveries);
            const rate = planned > 0 ? ((actual / planned) * 100).toFixed(1) : 'N/A';
            const rateColor = successColor(actual, planned);
            return (
              <tr key={r.trip_id} className={i % 2 === 0 ? 'report__tr--even' : 'report__tr--odd'}>
                <td className="report__td report__td--bold">{r.trip_id}</td>
                <td className="report__td">{r.vehicle_id}</td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': '#2980b9' } as React.CSSProperties}
                >
                  {r.total_deliveries}
                </td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': '#27ae60' } as React.CSSProperties}
                >
                  {r.actual_deliveries}
                </td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': '#e67e22' } as React.CSSProperties}
                >
                  {r.planned_deliveries}
                </td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': '#c0392b' } as React.CSSProperties}
                >
                  {r.cancelled_deliveries}
                </td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': '#e67e22' } as React.CSSProperties}
                >
                  {r.skipped_deliveries}
                </td>
                <td
                  className="report__td report__td--accent"
                  style={{ '--cell-color': rateColor } as React.CSSProperties}
                >
                  {rate}
                  {rate !== 'N/A' ? '%' : ''}
                </td>
                <td className="report__td">{fmt(r.delivery_start)}</td>
                <td className="report__td">{fmt(r.delivery_end)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="report__footer">
        <span>RouteSonar — Delivery Report</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

DeliveryReportTemplate.displayName = 'DeliveryReportTemplate';

const DeliveryReport = ({ dateRange, ...rest }: DeliveryReportProps): JSX.Element => {
  const templateRef = useRef<HTMLDivElement>(null);
  const { generate, loading } = useGeneratePDF(templateRef, {
    fileName: `delivery-report.pdf`,
    orientation: 'landscape',
    avoidRowCuts: true,
  });

  return (
    <>
      <DeliveryReportTemplate templateRef={templateRef} dateRange={dateRange} {...rest} />
      <Button type="default" icon={<FileDoneOutlined />} loading={loading} onClick={generate}>
        {loading ? 'Generating...' : 'Delivery Report'}
      </Button>
    </>
  );
};

DeliveryReport.defaultProps = {
  dateRange: undefined,
};

export default DeliveryReport;
